import { TableRows, ViewAgenda } from "@mui/icons-material";
import {
  Box,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React from "react";

import { monospaceStyle } from "../../theme";
import { ProductSort } from "../../types/productPlane";
import { ExploreViewMode } from "./exploreState";

interface ExploreResultsHeadProps {
  // Results that match the keyword and the filters
  total: number;
  // Results the search ran over
  scopeTotal: number;
  // What is counted, in the plural, e.g. "products"
  noun: string;
  sort: ProductSort;
  onSortChange: (sort: ProductSort) => void;
  view: ExploreViewMode;
  onViewChange: (view: ExploreViewMode) => void;
  // The control that exports the results; none, no export
  exportControl?: React.ReactNode;
}

const SORT_LABELS: Record<ProductSort, string> = {
  relevance: "Relevance",
  sources: "Most sources",
  latest: "Latest source",
};

const formatCount = (count: number): string => count.toLocaleString("en-US");

// A button of the view choice: its icon, then its name. Colours, corners and
// case come from the theme.
const VIEW_BUTTON_SX = { px: 1.5, gap: 0.75 } as const;

// The head of the results: the count as "N of M", the sort, and the choice
// between cards and table.
const ExploreResultsHead: React.FC<ExploreResultsHeadProps> = ({
  total,
  scopeTotal,
  noun,
  sort,
  onSortChange,
  view,
  onViewChange,
  exportControl,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
    <Typography role="status" variant="body2" color="text.secondary">
      <Box
        component="span"
        sx={{ ...monospaceStyle, fontSize: "1.25rem", color: "text.primary" }}
      >
        {formatCount(total)}
      </Box>
      {" of "}
      <Box component="span" sx={monospaceStyle}>
        {formatCount(scopeTotal)}
      </Box>
      {` ${noun}`}
    </Typography>

    <Box sx={{ flex: 1 }} />

    {total > 0 && exportControl}

    <Select
      size="small"
      value={sort}
      onChange={(event) => onSortChange(event.target.value as ProductSort)}
      inputProps={{ "aria-label": "Sort by" }}
      sx={{ minWidth: 160, fontSize: "0.875rem" }}
    >
      {(Object.keys(SORT_LABELS) as ProductSort[]).map((option) => (
        <MenuItem key={option} value={option}>
          {SORT_LABELS[option]}
        </MenuItem>
      ))}
    </Select>

    <ToggleButtonGroup
      exclusive
      size="small"
      value={view}
      aria-label="View"
      onChange={(_event, next: ExploreViewMode | null) => {
        // A click on the chosen view asks for none: keep the view
        if (next) {
          onViewChange(next);
        }
      }}
    >
      <ToggleButton value="cards" sx={VIEW_BUTTON_SX}>
        <ViewAgenda sx={{ fontSize: 16 }} />
        Cards
      </ToggleButton>
      <ToggleButton value="table" sx={VIEW_BUTTON_SX}>
        <TableRows sx={{ fontSize: 16 }} />
        Table
      </ToggleButton>
    </ToggleButtonGroup>
  </Box>
);

export default ExploreResultsHead;
